import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/delivery.scss";
import "../css/returns.scss";

function Returns() {
  return (
    <main className="policy-page policy-page--returns">
      <header className="policy-hero">
        <div>
          <p className="policy-eyebrow">Customer care · {siteConfig.brandName}</p>
          <h1>Returns &amp; refunds</h1>
        </div>
        <div className="policy-hero__intro">
          <p>
            Sample guidance for changing your mind, reporting damage and understanding
            how a production refund would work.
          </p>
          <span>Last updated 13 August 2026</span>
        </div>
      </header>

      <div className="policy-shell">
        <aside className="policy-sidebar">
          <nav aria-label="Customer policies">
            <p>Customer policies</p>
            <Link to="/shipping">Shipping &amp; delivery</Link>
            <Link className="is-active" to="/returns" aria-current="page">
              Returns &amp; refunds
            </Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </nav>
          <nav className="policy-toc" aria-label="On this page">
            <p>On this page</p>
            <a href="#returns-window">Return window</a>
            <a href="#returns-start">Start a return</a>
            <a href="#returns-condition">Item condition</a>
            <a href="#returns-refunds">Refunds</a>
            <a href="#returns-damage">Damage &amp; exceptions</a>
          </nav>
        </aside>

        <article className="policy-content">
          <div className="policy-demo-note" role="note">
            <strong>Demo storefront notice</strong>
            <p>
              This frontend cannot create a return, issue a label or refund a payment.
              The policy below is sample commerce content and is not a live offer.
            </p>
          </div>

          <section id="returns-window">
            <p className="policy-section-number">01</p>
            <h2>A considered 30-day window</h2>
            <p>
              The sample policy allows a return request within 30 calendar days of
              confirmed delivery. Items should remain unused, complete and in their
              original saleable condition. This does not limit any mandatory consumer
              rights that may apply in a buyer’s location.
            </p>
            <div className="policy-pullquote">
              <strong>30 days</strong>
              <span>Sample period from confirmed delivery to return request.</span>
            </div>
          </section>

          <section id="returns-start">
            <p className="policy-section-number">02</p>
            <h2>How a return would begin</h2>
            <ol>
              <li>
                <strong>Contact customer care.</strong> Include the order reference and
                the item you wish to return.
              </li>
              <li>
                <strong>Wait for instructions.</strong> A production team would confirm
                eligibility and provide the correct return address or label.
              </li>
              <li>
                <strong>Pack it carefully.</strong> Reuse the original protective
                packaging and retain proof of postage until the return is resolved.
              </li>
            </ol>
            <p>
              Parcels sent without confirmation may be delayed or sent to the wrong
              facility. In this demo, use the <Link to="/contact">contact page</Link> to
              test the enquiry form; it does not transmit the request.
            </p>
          </section>

          <section id="returns-condition">
            <p className="policy-section-number">03</p>
            <h2>Condition &amp; careful packaging</h2>
            <div className="policy-cards">
              <div className="policy-card">
                <span>Returnable condition</span>
                <strong>Unused and complete</strong>
                <p>Include original components, inserts and protective packaging.</p>
              </div>
              <div className="policy-card">
                <span>Handle with care</span>
                <strong>Protect every surface</strong>
                <p>Fragile pieces should not move inside the outer shipping box.</p>
              </div>
            </div>
            <p>
              A production seller may reduce a refund when handling goes beyond what
              is reasonably necessary to inspect an item, where permitted by law.
            </p>
          </section>

          <section id="returns-refunds">
            <p className="policy-section-number">04</p>
            <h2>Inspection &amp; refunds</h2>
            <p>
              After arrival, a production return would be inspected and its outcome
              confirmed by email. Approved refunds would be sent to the original
              payment method. Banks and payment providers can require an additional
              5–10 business days to display funds after a refund is initiated.
            </p>
            <p>
              Original expedited delivery charges and return postage are
              generally not refundable unless required by law or the item was faulty.
              This demo never captures payment, so it cannot initiate a refund.
            </p>
          </section>

          <section id="returns-damage">
            <p className="policy-section-number">05</p>
            <h2>Damage, faults &amp; exceptions</h2>
            <p>
              For damage or an incorrect item, keep all packaging and send clear images
              promptly. Handmade variation, natural texture and minor colour differences
              between a screen and a physical object are not automatically defects.
            </p>
            <p>The following sample categories may be excluded from change-of-mind returns:</p>
            <ul>
              <li>Personalised, made-to-order or altered objects.</li>
              <li>Gift cards and clearly marked final-sale items, where lawful.</li>
              <li>Used goods or incomplete sets that cannot be resold safely.</li>
            </ul>
            <div className="policy-contact-box">
              <div>
                <span>Questions about an object?</span>
                <strong>{siteConfig.contact.email}</strong>
              </div>
              <Link to="/contact">Contact customer care</Link>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Returns;
